/* global analytics */
import {datadogRum} from '@datadog/browser-rum';

const {currentUser, segmentToken, segmentCategory} = window;

// To test client side segment events in localdev, change 'production' to 'development' below.
const isProd = process.env.NODE_ENV === 'production';

/* Snippet from https://segment.com/docs/sources/website/analytics.js/quickstart/ */
if (isProd) {
    /* eslint-disable */
    !function () {
// define the key where the global analytics object will be accessible
        // customers can safely set this to be something else if need be
        var globalAnalyticsKey = "analytics"

        // Create a queue, but don't obliterate an existing one!
        var analytics = window[globalAnalyticsKey] = window[globalAnalyticsKey] || [];

        // If the real analytics.js is already on the page return.
        if (analytics.initialize) return;

        // If the snippet was invoked already show an error.
        if (analytics.invoked) {
            if (window.console && console.error) {
                console.error("Segment snippet included twice.");
            }
            return;
        }

        // Invoked flag, to make sure the snippet
        // is never invoked twice.
        analytics.invoked = true;

        // A list of the methods in Analytics.js to stub.
        analytics.methods = [
            "trackSubmit",
            "trackClick",
            "trackLink",
            "trackForm",
            "pageview",
            "identify",
            "reset",
            "group",
            "track",
            "ready",
            "alias",
            "debug",
            "page",
            "screen",
            "once",
            "off",
            "on",
            "addSourceMiddleware",
            "addIntegrationMiddleware",
            "setAnonymousId",
            "addDestinationMiddleware",
            "register"
        ];

        // Define a factory to create stubs. These are placeholders
        // for methods in Analytics.js so that you never have to wait
        // for it to load to actually record data. The `method` is
        // stored as the first argument, so we can replay the data.
        analytics.factory = function(e) {
            return function() {
                if (window[globalAnalyticsKey].initialized) {
                    // Sometimes users assigned analytics to a variable before analytics is done loading, resulting in a stale reference.
                    // If so, proxy any calls to the 'real' analytics instance.
                    return window[globalAnalyticsKey][e].apply(window[globalAnalyticsKey], arguments);
                }
                var args = Array.prototype.slice.call(arguments);

                // Add buffered page context object so page information is always up-to-date
                if (["track", "screen", "alias", "group", "page", "identify"].indexOf(e) > -1) {
                    var c = document.querySelector("link[rel='canonical']");
                    args.push({
                        __t: "bpc",
                        c: c && c.getAttribute("href") || undefined,
                        p: location.pathname,
                        u: location.href,
                        s: location.search,
                        t: document.title,
                        r: document.referrer
                    });
                }

                args.unshift(e);
                analytics.push(args);
                return analytics;
            };
        };


        // For each of our methods, generate a queueing stub.
        for (var i = 0; i < analytics.methods.length; i++) {
            var key = analytics.methods[i];
            analytics[key] = analytics.factory(key);
        }

        // Define a method to load Analytics.js from our CDN,
        // and that will be sure to only ever load it once.
        analytics.load = function(key, options) {
            // Create an async script element based on your key.
            var t = document.createElement("script");
            t.type = "text/javascript";
            t.async = true;
            t.setAttribute("data-global-segment-analytics-key", globalAnalyticsKey)
            t.src = "https://cdn.segment.com/analytics.js/v1/" + key + "/analytics.min.js";

            // Insert our script next to the first script element.
            var first = document.getElementsByTagName("script")[0];
            first.parentNode.insertBefore(t, first);
            analytics._loadOptions = options;
        };
        analytics._writeKey = segmentToken;



        // Add a version to keep track of what's in the wild.
        analytics.SNIPPET_VERSION = "5.2.1";

        // Load Analytics.js with your key, which will automatically
        // load the tools you've enabled for your account. Boosh!
        analytics.load(segmentToken);

    }();
    /* eslint-enable */
    analytics.debug(false);
}


// Context data is automatically generated except for the "source".
const state = {
    user: currentUser && currentUser.user ? currentUser.user : currentUser,
    loggedId: Boolean(currentUser?.accountUuid),//If there is an accountId the user is logged in
};

/**
 * By default, traits are cached in the browser’s local storage and attached to each subsequent identify call.
 * @return {object} - common user data for tracking
 */
function _getUserTraits() {
    return {
        'Account ID': state.user.fspAdminUuid || state.user.accountUuid,
        'source': segmentCategory,
    };
}

function _getCategory() {
    return {context: {'source': segmentCategory}};
}

/*
 * Note: Segment recommends against using identify for anonymous visitors to your site. analytics.js automatically retrieves
 * an anonymousId from localStorage or assigns one for new visitors. It will be attached to all page and track events
 * both before and after an identify.
 */
export function logIdentity() {
    if (isProd) {
        if (state.user) {
            analytics.identify(state.user.fspAdminUuid || state.user.accountUuid, _getUserTraits(), _getCategory());
            state.loggedId = true;
        }
    }
}

export function idReset() {
    if (isProd) {
        analytics.reset();
        state.loggedId = false;
    }
}

export function updateUserTraits(user) {
    state.user = Object.assign({}, state.user, user);
    logIdentity();
}

// Ensure Segment knows who the user is on every page load, not just after login/purchase flows.
logIdentity();

function recheckIdentity() {
    if(window.analytics.initialize) {
        const analyticsId = analytics?.user()?.id();
        if ((currentUser.fspAdminUuid || currentUser.accountUuid) !== analyticsId) {
            state.user = currentUser;
            logIdentity();
        }
    }
}

function _setDefaultProps(eventProps) {
    recheckIdentity();
    eventProps.source = segmentCategory;
    eventProps.traits = state.user ? _getUserTraits() : {};
}

/**
 **** ATTENTION **** PLEASE CONFORM TO THESE STANDARDS ****
 1. Prepend the functional area to the event name. (UPDATE: This is no longer
 a requirement for new events. Opt for setting the eventSourcePage property instead)
 2. Use plain english.
 'Agreed to Disclaimer' vs 'DISCLAIMER_AGREED'
 'Agreed to Disclaimer' vs 'Disclaimer Agreed'
 3. Use "Title Case".
 'Added Certificate' vs 'add certificate'
 'Agreed to Disclaimer' vs 'disclaimer agreed'
 4. Describe the action with as few words as possible.
 'Added Certificate' vs 'added new cert'
 5. Keep the name short and concise (three to four words).

 @param {string} eventName
 @param {Record<string, unknown>} eventProps
 */
export function logEvent(/** @type {string} */ eventName, /** @type {Record<string, unknown>} */ eventProps = {}) {
    if (isProd && state.user?.accountUuid) {
        analytics.ready(function() {
            const userId = analytics.user()?.id();
            if (userId) {
                _setDefaultProps(eventProps);
                analytics.track(eventName, eventProps, _getCategory());
                eventProps.action = eventProps.action || "event";
                // datadogRum.addAction(eventName, eventProps);
            }
            // Note: Silently skip if userId not available (Segment still loading or not identified)
        });
    }
}

export function logClick(linkElement, eventName, eventProps = {}) {
    if(isProd && state.user?.accountUuid) {
        analytics.ready(function() {
            const user = analytics.user();
            const userId = user?.id();
            if (userId) {
                _setDefaultProps(eventProps);
                analytics.trackLink(linkElement, eventName, eventProps);
                eventProps.action = "click";
                // datadogRum.addAction(eventName, eventProps);
            }
            // Note: Silently skip if userId not available (Segment still loading)
        });
    }
}

/*
    **** ATTENTION **** PLEASE CONFORM TO THESE STANDARDS ****
    category = "FFWeb", "Purchase" or "WWW"
    pageName = 'Simple Name' in title case.
    props = add any properties you would like.  url, path and page title will be auto appended.
*/
export function logPageView(pageName, passedCategory = null, props = {}) {
    if (isProd && state.user?.accountUuid) {
        analytics.ready(function() {
            const userId = analytics.user()?.id();
            if (userId) {
                const category = passedCategory ? passedCategory : segmentCategory;
                const page = window.location;
                const properties = Object.assign({}, props);
                properties.url = page.href;
                properties.path = page.pathname;
                properties.title = document.title;
                properties.traits = state.user ? _getUserTraits() : {};
                properties.email = '';
                analytics.page(category, pageName, properties, _getCategory());
                props.action = "view";
                datadogRum.addAction(pageName, props);
            }
            // Note: Silently skip if userId not available (Segment still loading or not identified)
        });
    }
}

function logTripEventProps(trip) {
    const eventProps = {};
    if (trip) {
        eventProps.flightObjectId = trip.flights[0].objectId;
        eventProps.tripObjectId = trip.objectId;
    }
    return eventProps;
}

// NOTE: There are many more constants that could be defined here, but currently
// are hard-coded wherever they are used. See FFAnalyticsManager+Flights.m for a
// more complete list of constants used in the iOS app. Hopefully they line up.
// NOTE: Honestly, I hate the wrapper function to prepend a prefix; we should
// just encode it in the constants imho, but I'm mimicing the iOS code here.
export const FLIGHTS_NAVLOG_SEND_BUTTON_CLICKED = 'Navlog Send To System Share';
export const FLIGHTS_BRIEFING_SEND_BUTTON_CLICKED = 'Briefing Send To System Share';
export const FLIGHTS_FILE_FORM_SEND_BUTTON_CLICKED = 'File Form Send To System Share';

/**
 * logFlightEvent - mimics iOS function in FFAnalyticsManager+Flights.m
 * @param {string} eventType - name of event to log
 * @param {trip} trip - trip object that contains flight
 * @param {object} eventProps - collection of data to pass along as event payload
 */
export function logFlightEvent(eventType, trip, eventProps = {}) {
    logEvent('Flights, ' + eventType, {
        ...eventProps,
        ...logTripEventProps(trip)
    });
}

export function logAttachmentEvent(eventType, flight, eventProps = {}) {
    logEvent('Flights, Attachments ' + eventType, {
        flightObjectId: flight.objectId,
        tripObjectId: flight.trip,
        ...eventProps,
    });
}

/**
 * logAircraftEvent
 * @param {string} eventType - name of event to log
 * @param {object} eventProps - collection of data to pass along as event payload
 */
export function logAircraftEvent(eventType, eventProps = {}) {
    logEvent('Aircraft, ' + eventType, eventProps);
}


/**
 * logMarkedPositionEvent
 * @param {string} eventType - name of event to log
 * @param {object} eventProps - collection of data to pass along as event payload
 */
export function logMarkedPositionEvent(eventType, eventProps = {}) {
    // NOTE: See: ffm/Classes/FFAnalyticsManager+MarkedPosition.h for reference,
    // but these events are supposed to be sent uppercase, with underscores for
    // spaces, unlike Flights or Aircraft events. There is no reason for the
    // inconsistency other than that's how it is on the iOS side, so we will
    // match it.
    //
    // We have this quick conversion in place to help coerce but also help
    // document what we expect.
    //
    // Also, unlike other event text, there is no common prefix text for these.
    eventType = eventType.trim();
    eventType = eventType.replace(/\s+/gi, '_').toUpperCase();
    logEvent('Marked Position, ' + eventType, eventProps);
}

export default {
    logEvent,
    logFlightEvent,
    logAircraftEvent,
    logAttachmentEvent,
    logMarkedPositionEvent,
    logIdentity,
    logPageView,
    updateUserTraits,
    idReset
};
